/**
 * Cognito PKCE auth utilities.
 *
 * Environment variables (set at build time via REACT_APP_*):
 *   REACT_APP_COGNITO_DOMAIN   e.g. "xylosnap-portal-dev-123456789.auth.eu-central-1.amazoncognito.com"
 *   REACT_APP_COGNITO_CLIENT_ID  from CDK output UserPoolClientId
 *   REACT_APP_REDIRECT_URI     e.g. "https://dev-portal.xylosnap.com"
 */
import * as Sentry from '@sentry/react';

// Strip any protocol prefix — we always use https://
const _rawDomain = process.env.REACT_APP_COGNITO_DOMAIN ?? '';
const COGNITO_DOMAIN = _rawDomain.replace(/^https?:\/\//, '').replace(/\/$/, '');

const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID ?? '';

// Ensure redirect URI always has https:// protocol and no trailing slash
const _rawRedirect = (process.env.REACT_APP_REDIRECT_URI ?? window.location.origin).replace(/\/$/, '');
const REDIRECT_URI = _rawRedirect.startsWith('http') ? _rawRedirect : `https://${_rawRedirect}`;

const TOKEN_KEY    = 'xt_id_token';
const EXPIRY_KEY   = 'xt_token_expiry';
// sessionStorage: per-tab, never shared across tabs or windows.
// This guarantees the verifier that generated the challenge is always
// the same verifier that gets used in the token exchange.
const VERIFIER_KEY = 'xt_pkce_verifier';

// Module-level guard: prevents a second concurrent initiateLogin() from
// overwriting the verifier that the first call already stored.
let _loginInProgress = false;

// ── PKCE helpers ──────────────────────────────────────────────────────────────

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (let i = 0; i < bytes.length; i++) {
    str += String.fromCharCode(bytes[i]);
  }
  return btoa(str)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function generateVerifier(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array.buffer);
}

async function generateChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(hash);
}

// ── Public API ────────────────────────────────────────────────────────────────

export function getToken(): string | null {
  const token = localStorage.getItem(TOKEN_KEY);
  const expiry = localStorage.getItem(EXPIRY_KEY);
  if (!token || !expiry) return null;
  if (Date.now() > parseInt(expiry, 10)) {
    clearToken();
    return null;
  }
  return token;
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EXPIRY_KEY);
  sessionStorage.removeItem(VERIFIER_KEY);
  _loginInProgress = false;
}

export async function initiateLogin(): Promise<void> {
  // Guard against concurrent calls that would overwrite the verifier.
  if (_loginInProgress) return;
  _loginInProgress = true;

  const verifier  = generateVerifier();
  const challenge = await generateChallenge(verifier);

  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem('xt_pkce_challenge', challenge);

  console.log('[auth] initiateLogin: challenge=' + challenge.slice(0, 12) + '... redirect_uri=' + REDIRECT_URI + ' client_id=' + CLIENT_ID);

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    scope: 'openid email',
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });

  window.location.href = `https://${COGNITO_DOMAIN}/oauth2/authorize?${params}`;
}

export interface CallbackResult {
  ok: boolean;
  error?: string;
}

export async function handleCallback(code: string): Promise<CallbackResult> {
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  if (!verifier) {
    return { ok: false, error: 'PKCE verifier ontbreekt — sessie verlopen. Probeer opnieuw.' };
  }

  try {
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: CLIENT_ID,
      code,
      redirect_uri: REDIRECT_URI,
      code_verifier: verifier,
    });

    console.log('[auth] handleCallback: code=' + code.slice(0, 8) + '... redirect_uri=' + REDIRECT_URI + ' client_id=' + CLIENT_ID);

    const res = await fetch(`https://${COGNITO_DOMAIN}/oauth2/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });

    if (!res.ok) {
      const rawText = await res.text();
      console.error('[auth] Token exchange failed. Status:', res.status, 'Response:', rawText);
      let errMsg = `HTTP ${res.status}`;
      let errCode: string | undefined;
      try {
        const errData = JSON.parse(rawText);
        errMsg = errData.error_description ?? errData.error ?? errMsg;
        errCode = errData.error;
      } catch { /* ignore parse error */ }
      // invalid_grant is normal "stale session, auto-retry happens" — info level.
      // Anything else is a real auth regression worth investigating.
      Sentry.captureMessage(`OAuth token exchange failed: ${errMsg}`, {
        level: errCode === 'invalid_grant' ? 'info' : 'error',
        tags: { source: 'cognito_oauth', failure: 'token_exchange', oauth_error: errCode ?? 'unknown', http_status: String(res.status) },
        extra: { responseBody: rawText.slice(0, 500) },
      });
      return { ok: false, error: errMsg };
    }

    const data = await res.json();
    const idToken: string = data.id_token;
    const expiresIn: number = data.expires_in ?? 3600;

    localStorage.setItem(TOKEN_KEY, idToken);
    localStorage.setItem(EXPIRY_KEY, String(Date.now() + expiresIn * 1000));
    sessionStorage.removeItem(VERIFIER_KEY);
    _loginInProgress = false;

    // Also set cookie so CloudFront function can detect auth state
    document.cookie = `id_token=${idToken}; path=/; SameSite=Lax; Secure; Max-Age=${expiresIn}`;

    return { ok: true };
  } catch (e: any) {
    const errMsg = e?.message ?? 'Netwerk- of CORS-fout bij token exchange';
    console.error('[auth] Token exchange exception:', errMsg);
    return { ok: false, error: errMsg };
  }
}

export function logout(): void {
  clearToken();
  document.cookie = 'id_token=; path=/; Max-Age=0';
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    logout_uri: REDIRECT_URI,
  });
  window.location.href = `https://${COGNITO_DOMAIN}/logout?${params}`;
}
